import { Injectable } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BackendDatasourceTypeMapper, DatasourceService } from '@y42/datasource';
import { environment } from '@y42/environment';
import { FeatureFlagService } from '@y42/feature-flag';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouteDetailTabs, Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { GitQuery } from '@y42/git';
import { TriggerService } from '@y42/impex';
import { DatasourceTypeEnum, Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import { Y42FeatureFlag } from '@y42/y42-feature-flag';
import {
  CreateIntegrationParams,
  IntegrationGitService,
  IntegrationQuery,
  IntegrationState,
  IntegrationStore,
} from '../../state/datasource';
import { IntegrationTableQuery, IntegrationTableStore } from '../../state/datasource-table';
import { IntegrationTableJobStore } from '../../state/datasource-table-job';
import { IntegrationTableJobService } from '../datasource-table-job/integration-table-job.service';
import { IntegrationAclService } from './integration-acl.service';
import { IntegrationUserDataService } from './integration-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class IntegrationService extends DatasourceService<IntegrationState> {
  constructor(
    private readonly featureFlagService: FeatureFlagService<Y42FeatureFlag>,
    protected readonly userData: IntegrationUserDataService,
    protected readonly store: IntegrationStore,
    protected readonly query: IntegrationQuery,
    protected readonly git: IntegrationGitService,
    protected readonly tableStore: IntegrationTableStore,
    protected readonly tableQuery: IntegrationTableQuery,
    protected readonly tableJobStore: IntegrationTableJobStore,
    protected readonly routerService: Y42RouterService,
    protected readonly toast: ToastService,
    protected readonly acl: IntegrationAclService,
    protected readonly gitQuery: GitQuery,
    triggerService: TriggerService,
    tableJobService: IntegrationTableJobService,
  ) {
    super(
      {
        featureType: Y42FeatureType.Integration,
        userData,
        store,
        query,
        toast,
        acl,
        git,
        tableStore,
        tableQuery,
        tableJobStore,
        tableJobService,
        gitQuery,
      },
      triggerService,
    );
  }

  async create({ title, description, typeModel }: CreateIntegrationParams) {
    const $name = getSafeString(title);
    const integration = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      name: title,
      description,
      settings: {
        $schema: this.getUrl(typeModel.type, 'source'),
      },
    });
    await this.store.pending;

    await this.git.commitChanges(
      `integrations: new ${typeModel.displayName} integration "${$name}" created`,
      integration.$path,
    );

    await this.routerService.navigateToDetail(Y42Routes.Integration, $name, Y42RouteDetailTabs.Overview);
  }

  async delete($path: string = this.query.getActiveId() as getIDType<IntegrationState>): Promise<void> {
    const entity = this.query.getEntity($path);
    await super.delete($path);
    return this.git.commitChanges(`integrations: integration '${entity?.$name ?? $path}' deleted`, [$path]);
  }

  getUrl(type: DatasourceTypeEnum, suffix: 'source' | 'secret') {
    const backendCompatibleType = BackendDatasourceTypeMapper(type);
    const anchor = type !== backendCompatibleType ? `#${type}` : '';
    // TODO figure out versioning
    const version = this.featureFlagService.get(Y42FeatureFlag.EnableDraftIntegrationConfigs) ? 'draft' : '0.0.0';
    return `${environment.url.json_schema}/integrations/${backendCompatibleType}/${version}/${suffix}.json${anchor}`;
  }
}
